import type { GameMeta } from '@awwyeah/shared';

/**
 * How the Apps tab orders the games still in play (owner ask 2026-09-25): the ones waiting on
 * you first, then most recently touched — and anything nobody has moved in a week folds into
 * a closed "Quiet games" section at the bottom.
 *
 * **Quiet is derived, never stored.** There's no "archived" flag and no timer that writes
 * anything: a game is quiet only because its newest move is old, so the first move anyone
 * makes brings it straight back up. Nothing is deleted, and nothing here counts days for
 * display — `isQuiet` answers yes/no and the UI says "Quiet games · 3", never "9 days!"
 * (`memory/no-demand-mechanics.md`).
 */

/** A game with no move for this long folds into "Quiet games". */
export const QUIET_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/** What a list row learns by opening the game's move log — the part `GameMeta` can't say. */
export interface GameListFacts {
  isMyTurn: boolean;
  /** The newest client-stamped `at` in the move log, or `null` (no moves, or legacy moves
   * written before moves carried a timestamp). */
  lastMoveAt: string | null;
}

function parseMs(iso: string | null | undefined): number {
  if (!iso) return Number.NaN;
  return Date.parse(iso);
}

/** Newest `at` in a move log. Moves are appended in roughly time order, but a CRDT merge of
 * two offline clients can interleave them, so this takes the max rather than the last. */
export function latestMoveAt(moves: readonly unknown[]): string | null {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const move of moves) {
    const at = (move as { at?: unknown } | null)?.at;
    if (typeof at !== 'string') continue;
    const ms = Date.parse(at);
    if (Number.isFinite(ms) && ms > bestMs) {
      bestMs = ms;
      best = at;
    }
  }
  return best;
}

/**
 * Epoch ms of the last sign of life: the newest move, or the registry entry's own
 * `updatedAt` (creation, a guest joining) when that's newer or there are no stamped moves.
 */
export function lastActivityMs(game: GameMeta, facts: GameListFacts | undefined): number {
  const candidates = [parseMs(facts?.lastMoveAt), parseMs(game.updatedAt), parseMs(game.createdAt)];
  const valid = candidates.filter(Number.isFinite);
  return valid.length > 0 ? Math.max(...valid) : 0;
}

/**
 * `facts` missing means the row hasn't read the move log yet (first paint, IndexedDB still
 * loading) — stay in the main list until it has, rather than folding a game away on the
 * registry's stale `updatedAt` and popping it back out a moment later.
 */
export function isQuiet(game: GameMeta, facts: GameListFacts | undefined, nowMs: number): boolean {
  if (!facts) return false;
  return nowMs - lastActivityMs(game, facts) >= QUIET_AFTER_MS;
}

/** Your move first, then most recently active; id breaks ties so the order never jitters. */
export function sortActiveGames(
  games: readonly GameMeta[],
  factsById: Readonly<Record<string, GameListFacts>>,
): GameMeta[] {
  return [...games].sort((a, b) => {
    const aTurn = factsById[a.id]?.isMyTurn ? 1 : 0;
    const bTurn = factsById[b.id]?.isMyTurn ? 1 : 0;
    if (aTurn !== bTurn) return bTurn - aTurn;
    const diff = lastActivityMs(b, factsById[b.id]) - lastActivityMs(a, factsById[a.id]);
    if (diff !== 0) return diff;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** The two halves of the in-play list, each already sorted. */
export function arrangeActiveGames(
  games: readonly GameMeta[],
  factsById: Readonly<Record<string, GameListFacts>>,
  nowMs: number,
): { live: GameMeta[]; quiet: GameMeta[] } {
  const sorted = sortActiveGames(games, factsById);
  return {
    live: sorted.filter((g) => !isQuiet(g, factsById[g.id], nowMs)),
    quiet: sorted.filter((g) => isQuiet(g, factsById[g.id], nowMs)),
  };
}
